'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIToggle from 'UIComponents/input/UIToggle';
import VideoField from 'ContentComponents/cmv2/fields/Video';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import ToolFormLabel from 'DesignComponents/tool/ToolFormLabel';
import UIAlert from 'UIComponents/alert/UIAlert';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import { VIDEO } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DEFAULT, RESIZABLE, SHOW_PREVIEW } from 'DesignData/enums/CustomWidgetFieldAttributes';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { SHOW_ADVANCED_OPTIONS } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { getHasDismissedAdvancedVideoOptionsWarning } from 'DesignData/selectors/app';
import { dismissAdvancedVideoOptionsWarning } from 'DesignData/actions/Application';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

class VideoContentOptions extends PureComponent {
  constructor(...args) {
    super(...args);

    this.onResizableChange = e => {
      this.props.editField({
        [RESIZABLE]: e.target.checked
      });
    };

    this.onShowPreviewChange = e => {
      this.props.editField({
        [SHOW_PREVIEW]: e.target.checked
      });
    };

    this.onDefaultChange = videoData => {
      this.props.editField({
        [DEFAULT]: videoData
      });
    };

    this.onChangeShowAdvancedOptions = e => {
      this.props.editField({
        [SHOW_ADVANCED_OPTIONS]: e.target.checked
      });
    };

    this.onAdvancedVideoOptionsWarningClose = () => {
      this.props.dismissAdvancedVideoOptionsWarning();
    };
  }

  renderSizeControlToggle() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-3",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.sizeControlOptionsLabel')
        })
      }), /*#__PURE__*/_jsx(SidebarInput, {
        checked: field[RESIZABLE],
        defaultChecked: true,
        disabled: readOnly,
        inputComponent: UIToggle,
        onChange: this.onResizableChange,
        required: true,
        size: "extra-small"
      })]
    });
  }

  renderShowPreviewToggle() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-3",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.showPreview.labelVideo')
        })
      }), /*#__PURE__*/_jsx(SidebarInput, {
        checked: field[SHOW_PREVIEW],
        defaultChecked: true,
        disabled: readOnly,
        inputComponent: UIToggle,
        onChange: this.onShowPreviewChange,
        required: true,
        size: "extra-small"
      })]
    });
  }

  renderShowAdvancedVideoOptionsToggle() {
    const {
      field,
      hasDismissedAdvancedVideoOptionsWarning
    } = this.props;
    const showAdvancedVideoOptionsWarning = !hasDismissedAdvancedVideoOptionsWarning && field[SHOW_ADVANCED_OPTIONS];
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-3",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.showAdvancedOptions')
        })
      }), /*#__PURE__*/_jsx(UIToggle, {
        checked: field[SHOW_ADVANCED_OPTIONS],
        "data-test-id": "module-field-editor-advanced-options-toggle",
        onChange: this.onChangeShowAdvancedOptions,
        size: "extra-small"
      }), showAdvancedVideoOptionsWarning && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-3",
        type: "warning",
        closeable: true,
        onClose: this.onAdvancedVideoOptionsWarningClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.advancedVideoOptionsWarning')
        })
      })]
    });
  }

  render() {
    const {
      field,
      fieldErrors,
      readOnly
    } = this.props;
    return (
      /*#__PURE__*/
      // The expandable section that the video field uses expects itself to be within
      // the edit form component, so in order for the expandable section styling to be
      // correct these classnames must be included.
      _jsx("div", {
        className: "cmv2-edit-form cmv2-field-group cmv2-edit-form--with-padding",
        children: /*#__PURE__*/_jsxs(ToolFieldset, {
          children: [/*#__PURE__*/_jsx(FormControlWithErrors, {
            errors: fieldErrors[DEFAULT],
            children: /*#__PURE__*/_jsx(VideoField, {
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey(`defaultLabels.${VIDEO}`)
              }),
              onFieldChange: this.onDefaultChange,
              readOnly: readOnly,
              resizable: field[RESIZABLE],
              show_advanced_options: field[SHOW_ADVANCED_OPTIONS],
              show_preview: field[SHOW_PREVIEW],
              value: field[DEFAULT]
            })
          }), this.renderSizeControlToggle(), this.renderShowPreviewToggle(), this.renderShowAdvancedVideoOptionsToggle()]
        })
      })
    );
  }

}

VideoContentOptions.propTypes = {
  dismissAdvancedVideoOptionsWarning: PropTypes.func,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  hasDismissedAdvancedVideoOptionsWarning: PropTypes.bool,
  readOnly: PropTypes.bool
};

const mapStateToProps = state => ({
  hasDismissedAdvancedVideoOptionsWarning: getHasDismissedAdvancedVideoOptionsWarning(state)
});

const mapDispatchToProps = {
  dismissAdvancedVideoOptionsWarning
};
export default connect(mapStateToProps, mapDispatchToProps)(VideoContentOptions);